<template>
  <component v-bind="attributes" :is="attributes.is" :class="classes" :style="style">
    <span class="btn__wrap">
      <slot />

      <ShortcutInfo v-if="shortcut" :shortcut="shortcut" :size="size" />
    </span>
  </component>
</template>

<script lang="ts">
import { defineComponent, PropType, Prop } from 'vue';

const buttonColors = ['grey', 'blue', 'dark', 'ghost', 'ghost-subtle', 'ghost-dark', 'white', 'destructive'];

export const buttonColorProp: Prop<string> = {
  type: String,
  default: 'blue',
  validator(value: (typeof buttonColors)[0]) {
    return buttonColors.indexOf(value) !== -1;
  },
};

export default defineComponent({
  props: {
    to: {
      type: [String, Object],
      default: null,
    },

    href: {
      type: String,
      default: null,
    },

    theme: {
      type: String,
      default: 'light',
      validator(value) {
        return ['light', 'dark'].indexOf(value) !== -1;
      },
    },

    color: buttonColorProp,

    icon: {
      type: Boolean,
      default: false,
    },

    size: {
      type: String,
      default: 'medium',
      validator(value) {
        return ['small', 'medium', 'large'].indexOf(value) !== -1;
      },
    },

    layout: {
      type: String,
      default: 'start',
      validator(value) {
        return ['start', 'center', 'space-between'].indexOf(value) !== -1;
      },
    },

    type: {
      type: String as PropType<'button' | 'submit'>,
      default: 'button',
      validator(value) {
        return ['button', 'submit'].includes(value);
      },
    },

    shortcut: {
      type: String,
      default: '',
    },
  },

  computed: {
    classes(): Array<string> {
      const size = this.icon ? `icon-${this.size}` : this.size;

      return ['btn', `btn--type-${this.color}`, `btn--size-${size}`, `btn--${this.theme}`];
    },

    style(): Record<string, string> {
      return {
        '--justify-content': this.layout,
      };
    },

    attributes():
      | { is: 'RouterLink'; to: string | Record<string, any>; target?: '_blank' | '_self' | '_parent' | '_top' }
      | { is: 'a'; href: string }
      | { is: 'button'; type: 'button' | 'submit' } {
      if (this.to) {
        return {
          is: 'RouterLink',
          to: this.to,
          target: this.to?.target,
        };
      }

      if (this.href) {
        return {
          is: 'a',
          href: this.href,
        };
      }

      return {
        is: 'button',
        type: this.type as 'button' | 'submit',
      };
    },
  },
});
</script>

<style lang="scss">
@use 'settings/colors';
@use 'settings/settings';
@use 'mixins/typography';
/**
 * Base class
 */
.btn {
  border: none;
  outline: none;
  cursor: pointer;
  margin: 0;
  padding: 0;
  border-radius: settings.$border-radius;
  transition: all 0.1s ease-in-out;
  white-space: nowrap;

  &:disabled {
    cursor: not-allowed;
  }
}

.btn__wrap {
  display: grid;
  grid-auto-flow: column;
  align-items: center;
  justify-content: var(--justify-content);
  height: 100%;
}

/**
 * Themes
 */
@media (prefers-color-scheme: dark) {
  .btn--type-grey.btn--dark {
    color: colors.$color-grey-50;
    background: colors.$color-grey-800;

    &:not(:disabled) {
      &:focus {
        color: colors.$color-grey-800;
      }

      &:hover,
      &.hover {
        color: colors.$color-grey-800;
      }
    }
  }

  .btn--type-ghost.btn--dark {
    color: colors.$color-grey-200;

    &:not(:disabled) {
      &:focus {
        color: colors.$color-grey-800;
      }

      &:hover,
      &.hover {
        color: colors.$color-grey-800;
      }
    }
  }
}

/**
 * Sizes
 */
.btn--size-large {
  height: 40px;
  padding: 0 12px;

  @include typography.typography-14-normal(160%);

  .btn__wrap {
    gap: 8px;
  }
}

.btn--size-medium {
  height: 32px;
  padding: 0 12px;

  @include typography.typography-14-normal(160%);

  .btn__wrap {
    gap: 8px;
  }
}

.btn--size-small {
  height: 24px;
  padding: 0 8px;

  @include typography.typography-14-normal(15.4px);

  .btn__wrap {
    grid-gap: 10px;
  }
}

@mixin icon-button {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0;
}

.btn--size-icon-medium {
  height: 32px;
  width: 32px;
  flex: 0 0 32px;

  @include icon-button;
}

.btn--size-icon-small {
  height: 24px;
  width: 24px;
  flex: 0 0 24px;

  @include icon-button;
}

/**
 * Colors
 */

/**
 * Blue
 */
.btn--type-blue {
  color: colors.$color-white;
  background-color: colors.$color-blue-600;

  &:not(:disabled) {
    &:hover,
    &.hover {
      background-color: colors.$color-blue-700;
    }

    &:focus {
      background-color: colors.$color-blue-700;
      box-shadow: 0px 0px 0px 3px rgba(colors.$color-blue-600, 0.2);
    }
  }

  &:disabled {
    color: colors.$color-blue-400;
    background-color: colors.$color-blue-100;
  }
}

/**
 * This is called "destructive" and not "red", because we previously has an option to make
 * all button colors destructive
 */
.btn--type-destructive {
  color: colors.$color-white;
  background-color: colors.$color-red-600;

  &:focus {
    background-color: colors.$color-red-600;
    box-shadow: 0px 0px 0px 3px rgba(colors.$color-red-600, 0.2);
  }

  &:hover:not(:disabled),
  &.hover:not(:disabled) {
    background-color: colors.$color-red-700;
  }

  &:disabled {
    color: rgba(colors.$color-red-600, 0.5);
    background-color: colors.$color-red-100;
  }
}

/**
 * Gray
 */
.btn--type-grey {
  color: colors.$color-grey-800;
  background-color: colors.$color-grey-100;

  // set inset shadow with 0 width so the transition can animate it
  &:not(:disabled) {
    &:hover,
    &.hover {
      background-color: colors.$color-grey-200;
    }

    &:focus {
      background-color: colors.$color-grey-300;
    }
  }

  &:disabled {
    color: colors.$color-grey-400;
    box-shadow: none;
  }
}

/**
 * Dark
 */
.btn--type-dark {
  color: colors.$color-grey-50;
  background-color: colors.$color-grey-600;

  // set inset shadow with 0 width so the transition can animate it
  &:not(:disabled) {
    &:hover,
    &.hover {
      background-color: colors.$color-grey-500;
    }

    &:focus {
      background-color: colors.$color-grey-400;
    }
  }

  &:disabled {
    color: colors.$color-grey-400;
    background-color: colors.$color-grey-600;
    box-shadow: none;
  }
}

/**
 * White
 */
.btn--type-white {
  color: colors.$color-grey-800;
  background-color: colors.$color-white;
  border: 1px solid colors.$color-grey-200;

  &:not(:disabled) {
    &:hover,
    &.hover {
      border: 1px solid colors.$color-grey-400;
    }

    &:focus {
      background-color: colors.$color-grey-100;
    }
  }

  &:disabled {
    color: colors.$color-grey-400;
    background-color: colors.$color-grey-100;
  }
}

/**
 * Ghost
 */
.btn--type-ghost {
  color: colors.$color-grey-900;
  background: transparent;
  box-shadow: none;

  & svg {
    color: colors.$color-grey-600;
  }

  &:not(:disabled) {
    &:hover,
    &.hover {
      background-color: colors.$color-grey-200;
    }

    &:focus {
      background-color: colors.$color-grey-300;
    }
  }

  &:disabled {
    & svg {
      color: colors.$color-grey-400;
    }
  }
}

/**
 * Ghost Subtle
 */
.btn--type-ghost-subtle {
  @extend .btn--type-ghost;

  color: colors.$color-grey-500;

  &:not(:disabled) {
    &:focus {
      color: colors.$color-grey-800;
    }

    &:hover,
    &.hover {
      color: colors.$color-grey-800;
    }
  }
}

/**
 * Ghost Dark
 */

.btn--type-ghost-dark {
  color: colors.$color-white;
  background: transparent;
  box-shadow: none;

  & svg {
    color: colors.$color-white;
  }

  &:not(:disabled) {
    &:hover,
    &.hover {
      background-color: colors.$color-grey-700;
    }

    &:focus {
      background-color: colors.$color-grey-600;
    }
  }

  &:disabled {
    color: colors.$color-grey-400;
    background-color: transparent;
  }
}
</style>
